/**
 * Data-driven question and atom mapping for one screening stage.
 */

import { useEffect, useMemo, useState } from "react";
import type {
  ScreeningAnswer,
  ScreeningStageResult,
} from "../../../api/clinicalTrialsApiTypes";
import type { ScreeningOverride } from "../../../data-model/eligibilityDetail";
import EvaluationSplitPane from "../shared/EvaluationSplitPane";
import {
  emptyEvaluationStageMessage,
  screeningStatusColors,
  screeningStatusLabel,
} from "../shared/screeningStatusDisplay";
import QuestionAndCriteria from "./primaryEvaluation/QuestionAndCriteria";

/** Props for one stage of the mapping-engine result. */
interface EvaluationStageTabProps {
  stage: ScreeningStageResult | undefined;
  overrides: ScreeningOverride[];
  heading: string;
}

/**
 * Label for the question list: the short protocol label, then the criterion sentence, then the id.
 *
 * @param question - Screening question result
 * @returns True when reasoning copy is present
 */
function questionHasReasoning(question: ScreeningAnswer): boolean {
  return Boolean(question.question_verbatim_text?.trim() || question.reasoning?.trim());
}

/**
 * Render one screening stage as a selectable question list and atom detail pane.
 *
 * @param props - Stage result, durable overrides, and display heading
 * @returns Mapping-engine stage UI
 */
export function EvaluationStageTab({
  stage,
  overrides,
  heading,
}: EvaluationStageTabProps) {
  const questions = stage?.questions ?? [];
  const visibleQuestions = useMemo(
    () => questions.filter(questionHasReasoning),
    [questions],
  );
  const [selectedQuestionId, setSelectedQuestionId] = useState(
    visibleQuestions[0]?.question_id ?? "",
  );

  useEffect(() => {
    setSelectedQuestionId((current) => {
      if (
        visibleQuestions.some((item) => item.question_id === current)
      ) {
        return current;
      }
      return visibleQuestions[0]?.question_id ?? "";
    });
  }, [visibleQuestions]);

  const selectedIndex = visibleQuestions.findIndex(
    (item) => item.question_id === selectedQuestionId,
  );
  const selected =
    visibleQuestions[selectedIndex >= 0 ? selectedIndex : 0] ??
    visibleQuestions[0];
  const selectedQuestionIndex = selectedIndex >= 0 ? selectedIndex + 1 : 1;

  if (!stage || visibleQuestions.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-primaryGray-14 bg-white p-6">
        <div className="text-center text-sm text-primaryGray-4">
          {emptyEvaluationStageMessage(heading, stage?.status)}
        </div>
      </div>
    );
  }

  const listItems = visibleQuestions.map((question, index) => {
    const status = question.status || question.answer || "NOT_EVALUATED";
    const colors = screeningStatusColors(status);
    return {
      id: question.question_id,
      positionText: `#${index + 1}`,
      criteriaDetails: question.label?.trim() || question.reasoning!.trim(),
      tagLabel: screeningStatusLabel(status),
      tagBackgroundColor: colors.backgroundColor,
      tagTextColor: colors.textColor,
    };
  });

  return (
    <EvaluationSplitPane
      heading={heading}
      subheading={`${visibleQuestions.length} criteria · ${stage.status}`}
      className="bg-white"
      items={listItems}
      selectedId={selectedQuestionId}
      onSelectedIdChange={setSelectedQuestionId}
    >
      {selected && (
        <QuestionAndCriteria
          question={selected}
          questionIndex={selectedQuestionIndex}
          overrides={overrides}
        />
      )}
    </EvaluationSplitPane>
  );
}
